import { GetServerSideProps, GetServerSidePropsContext } from "next";
import { useSession } from "@/components/useSession";
import { useRouter } from "next/router";
import { ErrorMessage } from "@/components/ErrorMessage";
import { ProviderType, User } from "@/types";
import { Template } from "@/types/template";
import { bool } from "@/lib/utils";

import { MarketingShell } from "@/components/landing/MarketingShell";
import { getTemplates } from "@/lib/templates";
import { getServerSession } from "@/lib/middleware";
import { getCurrentUser } from "@/lib/user";
import logger from "@/lib/logger";
import { Launch } from "@/components/launch/Launch";
import { ProvisionHost } from "@/components/launch/ProvisionHost";
import { ExistingHost } from "@/components/launch/ExistingHost";
import { ChooseHostingOption } from "@/components/launch/ChooseHostOrProvision";
import { TemplateCard } from "@/components/launch/TemplateCard";
import { SeoHead } from "@/components/useSeo";

export const Page = ({ template }: { template: Template | null }) => {
  const router = useRouter();
  const step = [router.query.id].flat()[1] ?? "template";
  const { data: session } = useSession();
  const provider = router.query.provider as ProviderType | undefined;

  const handleSetProvider = (provider: ProviderType) => {
    router.push(`/r/${template?.id}/${step}?provider=${provider}`);
  };

  if (!template) {
    return (
      <ErrorMessage error="Template not found" buttonText="See all templates" redirect="/r/" />
    );
  }

  return (
    <MarketingShell className="mt-20">
      <SeoHead
        path="r"
        title={"Deploy " + template.name}
        description={`Deploy ${template.name} in one click to your VPS.`}
        image={
          "/api/og/r?" +
          new URLSearchParams({
            ...(template.logo ? { logoUrl: template.logo } : {}),
            ...(template.name ? { title: template.name.trim() } : {}),
            ...(template.description
              ? { description: "Deploy " + template.name + ": " + template.intro.trim() }
              : {})
          }).toString()
        }
      />

      {step === "launch" ? (
        <Launch
          template={template}
          onNext={(step, args) => {
            const query = args
              ? new URLSearchParams(
                  Object.fromEntries(
                    Object.entries(args)
                      .filter(([, v]) => v !== undefined)
                      .map(([k, v]) => [k, String(v)])
                  )
                ).toString()
              : "";
            if (step === "host") {
              router.push(`/r/${template.id}/host?${query}`);
            } else if (step === "provision") {
              router.push(`/r/${template.id}/provision?${query}`);
            } else if (step === "existing") {
              router.push(`/r/${template.id}/existing?${query}`);
            }
          }}
        />
      ) : step === "provision" ? (
        <ProvisionHost
          template={template}
          provider={provider ?? "do"}
          setProvider={handleSetProvider}
          onNext={(step, options) =>
            router.push(
              `/r/${template.id}/${step}?${new URLSearchParams(
                Object.fromEntries(
                  Object.entries(options ?? {}).filter(([_, v]) => v !== undefined)
                )
              ).toString()}`
            )
          }
        />
      ) : step === "existing" ? (
        <ExistingHost
          template={template}
          onNext={(step, options) =>
            router.push(
              `/r/${template.id}/${step}?${new URLSearchParams(
                Object.fromEntries(
                  Object.entries(options ?? {}).filter(([_, v]) => v !== undefined)
                )
              ).toString()}`
            )
          }
        />
      ) : step === "host" ? (
        <ChooseHostingOption
          template={template}
          onNext={(step, provider) =>
            router.push(`/r/${template.id}/${step}?provider=${provider}`)
          }
        />
      ) : step === "template" ? (
        <TemplateCard template={template} user={session?.user} provider={provider} />
      ) : null}
    </MarketingShell>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  let user: Pick<User, "id" | "tenantId"> | undefined;
  const session = await getServerSession(
    {
      ...context.req,
      query: context.query
    },
    context.res
  );

  if (session?.user) {
    user = (await getCurrentUser(session.user.id, session.user.tenantId)) ?? undefined;
    if (!user) {
      logger.warn("User not found", session.user.id);
    }
  }

  const { id } = context.query;
  const templateId = typeof id === "string" ? id : Array.isArray(id) ? id[0] : undefined;
  const templates = await getTemplates(user, bool(context.query.skipCache));
  const template = id ? templates.find(t => t.id === templateId) || undefined : undefined;

  return {
    props: {
      template: template || null
    }
  };
};

export default Page;
